// useAggressivePlanCoincidence — for each Aggressive feed assignment with
// 2+ services, fetch the real time-aligned coincidence_peak_a via the
// Tier 1 endpoint (POST /api/windows/{id}/roster/subset-coincidence) and
// hand the result back to the planner UI.
//
// Why: the venue planner places services using sum(show_condition_max_a)
// for all three strategies. That's correct for Safest + Balanced (they
// want conservative sizing). Aggressive's job is to surface the diversity
// benefit — services don't actually peak simultaneously, so the real
// coincidence_peak_a on a feed is typically smaller than the sum. Without
// this overlay, "Aggressive" would just be "Balanced at a higher
// utilization target" — defeats the strategy gradient.
//
// Constraint: Tier 1 needs window_id + telemetry. Only meaningful when the
// target venue == source venue (the current event). For other venues there
// is no telemetry to time-align, so coincidence isn't available — Aggressive
// falls back to the sum just like Balanced (caller decides whether to mount
// the hook based on this).
//
// Singletons (assignments with one service) skip the call — their
// coincidence equals their sum trivially.

import { useEffect, useState } from "react";
import { subsetCoincidence } from "@/lib/api";
import type { Tier1Result } from "@/lib/types";
import type { VenuePlan } from "@/lib/venuePlanner";

export type AssignmentCoincidence =
  | { status: "loading" }
  | { status: "ready"; result: Tier1Result }
  | { status: "error"; message: string };

export type AssignmentCoincidenceMap = Record<string, AssignmentCoincidence>;

// Stable key for an assignment so the result merges back to the right row
// even as the operator changes venue / strategy / utilization. Mirrors
// the shape the planner uses to identify a feed-assignment uniquely.
export function assignmentKey(feedId: string, membershipIds: string[]): string {
  return `${feedId}|${[...membershipIds].sort().join(",")}`;
}

export function useAggressivePlanCoincidence(
  windowId: string | null,
  plan: VenuePlan | null,
  enabled: boolean,
): AssignmentCoincidenceMap {
  const [results, setResults] = useState<AssignmentCoincidenceMap>({});

  useEffect(() => {
    if (!enabled || !windowId || !plan) {
      setResults({});
      return;
    }
    // Build the set of (key, membership_ids) we need to fetch.
    const todo: Array<{ key: string; membershipIds: string[] }> = [];
    for (const part of plan.partitions) {
      for (const a of part.assignments) {
        const mids = a.services
          .map((s) => s.roster_membership_id)
          .filter((m): m is string => !!m);
        if (mids.length < 2) continue;
        todo.push({ key: assignmentKey(a.feed.feed_id, mids), membershipIds: mids });
      }
    }
    if (todo.length === 0) {
      setResults({});
      return;
    }
    // Mark all loading up front so the UI shows progress immediately.
    setResults(Object.fromEntries(todo.map((t) => [t.key, { status: "loading" }])));

    const abortControllers: AbortController[] = [];
    let cancelled = false;

    for (const { key, membershipIds } of todo) {
      const ctrl = new AbortController();
      abortControllers.push(ctrl);
      subsetCoincidence(
        windowId,
        { membership_ids: membershipIds },
        { signal: ctrl.signal },
      )
        .then((result) => {
          if (cancelled) return;
          setResults((prev) => ({ ...prev, [key]: { status: "ready", result } }));
        })
        .catch((err: Error) => {
          if (cancelled || err.name === "AbortError") return;
          setResults((prev) => ({
            ...prev,
            [key]: { status: "error", message: err.message },
          }));
        });
    }

    return () => {
      cancelled = true;
      for (const c of abortControllers) c.abort();
    };
  }, [windowId, plan, enabled]);

  return results;
}
